import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Breadcrumbs,
  Link,
  TextField,
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent,
  InputLabel,
  Grid,
  CircularProgress,
  Autocomplete,
  createFilterOptions,
  Stack,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  ErrorOutline as ErrorIcon,
  Home as HomeIcon,
} from '@mui/icons-material';

// --- Import the service function ---
// --- FIX: Added new import functions for save, update, and delete ---
import {
  getSnowAssignmentGroups,
  addSnowAssignmentGroup,
  updateSnowAssignmentGroup,
  deleteSnowAssignmentGroup
} from 'service/service';

// --- Import the new table components ---
import { CustomizedDataGrid } from 'components/customized_data_grid';
import RowTypography from 'components/Table/RowTypography';

// --- DATA INTERFACE (Unchanged) ---
interface RowData {
  id: number;
  assignmentGroup: string;
  domain: string;
  program: string;
  businessUnit: string;
  department: string;
  capability: string;
}

// --- DROPDOWN OPTIONS (Unchanged) ---
const domainOptions = ['Financial', 'Material', 'Consumer', 'Supplier', 'Customer', 'Supply Chain', 'HR', 'Legal', 'Sales', 'Marketing'];
const programOptions = ['Mosaic', 'IBP', 'Global'];
const businessUnitOptions = ['Foods', 'Beverages', 'Global'];
const departmentOptions = [
  'DEMP',
  'D&AI Arch & Governance',
  'BI & Reporting & Automation',
  'Data Science & Advance Analytics',
  'D&AI Product Management',
  'AI Solutions & Platform',
];
const capabilityOptions = ['AIOps', 'MLOps', 'BIOps', 'DataOps', 'IntegrationOps', 'PlatformOps'];

// --- Filter types for search (Unchanged) ---
const filterTypeOptions = [
  { label: 'Assignment Group', value: 'assignmentGroup' },
  { label: 'Domain', value: 'domain' },
  { label: 'Program', value: 'program' },
  { label: 'Business Unit', value: 'businessUnit' },
  { label: 'Department', value: 'department' },
  { label: 'Capability', value: 'capability' },
];

// --- 'starts with' filter (Unchanged) ---
const filterOptions = createFilterOptions({
  matchFrom: 'start',
  stringify: (option: string) => option,
});
// --- End Data ---


// --- STYLES (Unchanged) ---
const lightThemeInputStyles = {
  input: {
    color: 'black !important',
    fontWeight: 'normal',
    WebkitTextFillColor: 'black !important'
  },
  label: {
    color: 'black !important',
    fontWeight: 'normal',
    WebkitTextFillColor: 'black !important'
  },
  '& .MuiInputLabel-root': {
    color: 'black !important',
    fontWeight: 'normal',
    '&.Mui-focused': { color: '#1976d2 !important', fontWeight: 'normal' },
    '&.MuiInputLabel-shrink': { color: 'black !important', fontWeight: 'normal' },
    '&.MuiInputLabel-outlined': { color: 'black !important', fontWeight: 'normal' }
  },
  '& .MuiFormLabel-root': {
    color: 'black !important',
    fontWeight: 'normal',
    '&.Mui-focused': { color: '#1976d2 !important' }
  },
  '& .MuiOutlinedInput-input': {
    color: 'black !important',
    fontWeight: 'normal',
    WebkitTextFillColor: 'black !important'
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'white !important',
    fontWeight: 'normal',
    color: 'black !important',
    '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.23) !important' },
    '&:hover fieldset': { borderColor: 'rgba(0, 0, 0, 0.87) !important' },
    '&.Mui-focused fieldset': { borderColor: '#1976d2 !important' },
    '& input': {
      color: 'black !important',
      fontWeight: 'normal',
      WebkitTextFillColor: 'black !important'
    },
    '& .MuiInputBase-input': {
      color: 'black !important',
      fontWeight: 'normal',
      WebkitTextFillColor: 'black !important'
    },
    '& .MuiSelect-select': {
      color: 'black !important',
      fontWeight: 'normal',
      WebkitTextFillColor: 'black !important'
    }
  },
  '& .MuiSvgIcon-root': { color: 'rgba(0, 0, 0, 0.54) !important' },
  '& .MuiSelect-select': {
    color: 'black !important',
    fontWeight: 'normal',
    WebkitTextFillColor: 'black !important'
  },
};

const lightThemeSelectStyles = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'white !important',
    color: 'black !important',
    '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.23) !important' },
    '&:hover fieldset': { borderColor: 'rgba(0, 0, 0, 0.87) !important' },
    '&.Mui-focused fieldset': { borderColor: '#1976d2 !important' },
  },
  '& .MuiSelect-select': {
    color: 'black !important',
    fontWeight: 'normal',
    WebkitTextFillColor: 'black !important'
  },
  '& .MuiInputLabel-root': {
    color: 'black !important',
    fontWeight: 'normal',
  },
  '& .MuiSvgIcon-root': { color: 'rgba(0, 0, 0, 0.54) !important' },
};

const forceLightMenuProps = {
  sx: {
    '& .MuiPaper-root': {
      backgroundColor: 'white !important',
      color: 'black !important',
    },
    '& .MuiList-root': {
      backgroundColor: 'white !important',
      color: 'black !important',
    },
    '& .MuiMenuItem-root': {
      color: 'black !important',
      WebkitTextFillColor: 'black !important',
      backgroundColor: 'white !important',
      fontWeight: 'normal',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04) !important'
      },
      '&.Mui-selected': {
        backgroundColor: 'rgba(0, 0, 0, 0.08) !important',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04) !important'
        }
      },
      '&.Mui-focusVisible': {
        backgroundColor: 'rgba(0, 0, 0, 0.04) !important'
      }
    }
  }
};

const lightThemeMenuPropsAuto = {
  PaperProps: {
    sx: {
      backgroundColor: 'white !important',
      color: 'black !important',
      '& .MuiAutocomplete-option': {
        color: 'black !important',
        WebkitTextFillColor: 'black !important',
        backgroundColor: 'white !important',
        fontWeight: 'normal',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04) !important'
        },
        '&[aria-selected="true"]': {
          backgroundColor: 'rgba(0, 0, 0, 0.08) !important'
        }
      },
    }
  }
};
// --- END STYLES ---


// --- Main Governance Component ---
const Governance: React.FC = () => {
  const [activeTab, setActiveTab] = useState('snow');
  const [masterList, setMasterList] = useState<RowData[]>([]);
  const [rows, setRows] = useState<RowData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [editedRowData, setEditedRowData] = useState<Partial<RowData>>({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterType, setFilterType] = useState<keyof RowData | ''>('assignmentGroup');
  const [filterValue, setFilterValue] = useState('');
  const [filterValueOptions, setFilterValueOptions] = useState<string[]>([]);
  const [isSearchable, setIsSearchable] = useState(true);
  const [sortingInfo, setSortingInfo] = useState({ sortBy: '', sortDir: '' });

  const handleTabClick = (tabName: string) => {
    if (tabName === 'snow') {
      setActiveTab(tabName);
    }
  };

  const assignmentGroupOptions = useMemo(() =>
    Array.from(new Set(masterList.map((row) => row.assignmentGroup)))
    , [masterList]);

  useEffect(() => {
    if (activeTab === 'snow') {
      fetchAssignmentGroups();
    }
  }, [activeTab]);

  useEffect(() => {
    if (!filterType) return;
    setFilterValue('');
    if (filterType === 'assignmentGroup') {
      setIsSearchable(true);
      setFilterValueOptions([]);
    } else {
      setIsSearchable(false);
      const uniqueValues = Array.from(
        new Set(masterList.map(row => row[filterType as keyof RowData]).filter(Boolean))
      ) as string[];
      setFilterValueOptions(uniqueValues.sort());
    }
  }, [filterType, masterList]);

  const fetchAssignmentGroups = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getSnowAssignmentGroups();
      if (result.data && Array.isArray(result.data)) {
        const formattedData = result.data.map((item: any) => ({
          id: item.ID, // Make sure this matches the alias from your SQL query
          assignmentGroup: item.AssignmentGroup,
          domain: item.Domain,
          program: item.Program,
          businessUnit: item.BusinessUnit,
          department: item.Department,
          capability: item.Capability
        }));
        setMasterList(formattedData);
        setRows(formattedData);
      } else {
        throw new Error("Invalid data format received from server.");
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || err.message || "An unknown error occurred.";
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterTypeChange = (event: SelectChangeEvent<string>) => {
    setFilterType(event.target.value as keyof RowData | '');
  };

  const handleFilterValueChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>) => {
    setFilterValue(event.target.value);
  };

  const handleQuery = () => {
    setPage(0);
    if (!filterType || !filterValue) {
      setRows(masterList);
      return;
    }
    const lowerCaseValue = filterValue.toLowerCase();
    const filtered = masterList.filter((row) => {
      const rowValue = row[filterType as keyof RowData];
      if (rowValue) {
        if (isSearchable) {
          return String(rowValue).toLowerCase().startsWith(lowerCaseValue);
        } else {
          return String(rowValue).toLowerCase() === lowerCaseValue;
        }
      }
      return false;
    });
    setRows(filtered);
  };

  const handleClearFilter = () => {
    setPage(0);
    setFilterType('assignmentGroup');
    setFilterValue('');
    setRows(masterList);
  };

  const handleSorting = (sortInfo: any) => {
    setSortingInfo(sortInfo);
  };

  const handleAddNew = () => {
    if (editingRowId !== null && !masterList.some(row => row.id === editingRowId)) {
      return;
    }

    const newRowId = Date.now();
    const newRow: RowData = {
      id: newRowId,
      assignmentGroup: '',
      domain: '',
      program: '',
      businessUnit: '',
      department: '',
      capability: '',
    };

    setEditedRowData(newRow);
    setEditingRowId(newRowId);
    setRows([newRow, ...rows]);
    setPage(0);
  };

  // --- FIX: Implemented real delete ---
  const handleDeleteRow = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this row?')) {
      try {
        await deleteSnowAssignmentGroup(id);
        // Refresh data from DB after successful delete
        fetchAssignmentGroups();
      } catch (err) {
        alert("Failed to delete row. See console for details.");
        console.error("Delete error:", err);
      }
    }
  };

  const handleEditRow = (row: RowData) => {
    setEditingRowId(row.id);
    setEditedRowData(row);
  };

  const handleCancelEdit = () => {
    const isNewRow = !masterList.some(row => row.id === editingRowId);

    if (isNewRow) {
      setRows(rows.filter(row => row.id !== editingRowId));
    }

    setEditingRowId(null);
    setEditedRowData({});
  };

  // --- FIX: Implemented real save/update ---
  const handleSaveRow = async () => {
    if (!editedRowData.assignmentGroup || editedRowData.assignmentGroup.trim() === '') {
      alert('Assignment Group cannot be empty.');
      return;
    }

    const isNewRow = !masterList.some(row => row.id === editingRowId);

    const payload = {
      assignmentGroup: editedRowData.assignmentGroup || '',
      domain: editedRowData.domain || '',
      program: editedRowData.program || '',
      businessUnit: editedRowData.businessUnit || '',
      department: editedRowData.department || '',
      capability: editedRowData.capability || '',
    };

    try {
      if (isNewRow) {
        // --- Call ADD API ---
        await addSnowAssignmentGroup(payload);
      } else {
        // --- Call UPDATE API ---
        await updateSnowAssignmentGroup(editingRowId as number, payload);
      }

      setEditingRowId(null);
      setEditedRowData({});

      // Refresh all data from the database
      fetchAssignmentGroups();

    } catch (err) {
      console.error("Failed to save row:", err);
      alert("Error: Could not save the row. See console for details.");
    }
  };

  const handleSelectChange = (
    event: SelectChangeEvent<string>,
    field: keyof RowData
  ) => {
    setEditedRowData(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleAutocompleteChange = (newInputValue: string | null) => {
    setEditedRowData(prev => ({ ...prev, assignmentGroup: newInputValue || '' }));
  };

  const handleChangePage = (
    event: React.ChangeEvent<unknown> | null,
    newPage: number
  ) => {
    setPage(newPage - 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedRows = useMemo(
    () =>
      rowsPerPage > 0
        ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        : rows,
    [rows, page, rowsPerPage]
  );

  const isEditing = (id: number) => editingRowId === id;

  const getColumns = () => {
    const columns = [
      {
        key: 'assignmentGroup',
        name: 'ASSIGNMENT GROUP',
        resizable: true,
        minWidth: 250,
        formatter: ({ row }: { row: RowData }) => {
          const editing = isEditing(row.id);
          return editing ? (
            <Autocomplete
              freeSolo
              fullWidth
              size="small"
              options={assignmentGroupOptions}
              filterOptions={filterOptions}
              value={editedRowData.assignmentGroup || ''}
              onInputChange={(event, newInputValue) => {
                handleAutocompleteChange(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  autoFocus
                  sx={lightThemeInputStyles}
                />
              )}
              PaperComponent={(props) => (
                <Paper {...props} sx={lightThemeMenuPropsAuto.PaperProps.sx} />
              )}
            />
          ) : (
            <RowTypography>{row.assignmentGroup}</RowTypography>
          );
        },
      },
      {
        key: 'domain',
        name: 'DOMAIN',
        resizable: true,
        minWidth: 150,
        formatter: ({ row }: { row: RowData }) => {
          const editing = isEditing(row.id);
          return editing ? (
            <FormControl fullWidth size="small">
              <Select
                value={editedRowData.domain || ''}
                onChange={(e) => handleSelectChange(e, 'domain')}
                sx={lightThemeSelectStyles}
                MenuProps={forceLightMenuProps}
              >
                {domainOptions.map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <RowTypography>{row.domain}</RowTypography>
          );
        },
      },
      {
        key: 'program',
        name: 'PROGRAM',
        resizable: true,
        minWidth: 150,
        formatter: ({ row }: { row: RowData }) => {
          const editing = isEditing(row.id);
          return editing ? (
            <FormControl fullWidth size="small">
              <Select
                value={editedRowData.program || ''}
                onChange={(e) => handleSelectChange(e, 'program')}
                sx={lightThemeSelectStyles}
                MenuProps={forceLightMenuProps}
              >
                {programOptions.map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <RowTypography>{row.program}</RowTypography>
          );
        },
      },
      {
        key: 'businessUnit',
        name: 'BUSINESS UNIT',
        resizable: true,
        minWidth: 150,
        formatter: ({ row }: { row: RowData }) => {
          const editing = isEditing(row.id);
          return editing ? (
            <FormControl fullWidth size="small">
              <Select
                value={editedRowData.businessUnit || ''}
                onChange={(e) => handleSelectChange(e, 'businessUnit')}
                sx={lightThemeSelectStyles}
                MenuProps={forceLightMenuProps}
              >
                {businessUnitOptions.map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <RowTypography>{row.businessUnit}</RowTypography>
          );
        },
      },
      {
        key: 'department',
        name: 'DEPARTMENT',
        resizable: true,
        minWidth: 150,
        formatter: ({ row }: { row: RowData }) => {
          const editing = isEditing(row.id);
          return editing ? (
            <FormControl fullWidth size="small">
              <Select
                value={editedRowData.department || ''}
                onChange={(e) => handleSelectChange(e, 'department')}
                sx={lightThemeSelectStyles}
                MenuProps={forceLightMenuProps}
              >
                {departmentOptions.map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <RowTypography>{row.department}</RowTypography>
          );
        },
      },
      {
        key: 'capability',
        name: 'CAPABILITY',
        resizable: true,
        minWidth: 150,
        formatter: ({ row }: { row: RowData }) => {
          const editing = isEditing(row.id);
          return editing ? (
            <FormControl fullWidth size="small">
              <Select
                value={editedRowData.capability || ''}
                onChange={(e) => handleSelectChange(e, 'capability')}
                sx={lightThemeSelectStyles}
                MenuProps={forceLightMenuProps}
              >
                {capabilityOptions.map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <RowTypography>{row.capability}</RowTypography>
          );
        },
      },
      {
        key: 'actions',
        name: 'ACTIONS',
        resizable: false,
        width: 120,
        formatter: ({ row }: { row: RowData }) => {
          const editing = isEditing(row.id);
          return editing ? (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <IconButton onClick={handleSaveRow} aria-label="save row">
                <SaveIcon sx={{ color: '#0070C0' }} />
              </IconButton>
              <IconButton onClick={handleCancelEdit} aria-label="cancel edit">
                <CancelIcon sx={{ color: '#555' }} />
              </IconButton>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <IconButton onClick={() => handleEditRow(row)} aria-label="edit row">
                <EditIcon sx={{ color: '#0070C0' }} />
              </IconButton>
              <IconButton onClick={() => handleDeleteRow(row.id)} aria-label="delete row">
                <DeleteIcon sx={{ color: '#E60000' }} />
              </IconButton>
            </Box>
          );
        },
      },
    ];
    return columns;
  };


  return (
    <Box sx={{ p: 3, m: 0, mt: 3, backgroundColor: '#F8F9FA !important', minHeight: '100vh', color: '#000 !important' }}>

      {/* --- Breadcrumb (Unchanged) --- */}
      <Breadcrumbs
        aria-label="breadcrumb"
        sx={{
          mb: 2,
          '& .MuiBreadcrumbs-ol': {
            alignItems: 'center'
          },
          '& .MuiBreadcrumbs-li p': {
            color: '#FFFFFF !important',
            fontWeight: 500
          },
          '& .MuiBreadcrumbs-separator': {
            color: 'rgba(255, 255, 255, 0.7) !important'
          }
        }}
      >
        <Link
          underline="hover"
          sx={{ display: 'flex', alignItems: 'center', color: 'rgba(255, 255, 255, 0.7) !important' }}
          href="/"
        >
          <HomeIcon sx={{ mr: 0.5, fontSize: 'inherit' }} />
        </Link>
        <Typography
          sx={{ display: 'flex', alignItems: 'center', color: 'rgba(255, 255, 255, 0.7) !important' }}
        >
          D & AI MONTHLY XOPS
        </Typography>
        <Typography color="text.primary" sx={{ color: '#FFFFFF !important', fontWeight: 500 }}>
          GOVERNANCE & PLANNING
        </Typography>
      </Breadcrumbs>
      {/* --- END OF BREADCRUMB --- */}


      <Paper sx={{
        borderRadius: 2,
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        backgroundColor: '#FFFFFF !important',
        color: 'black !important'
      }}>

        {/* --- Tab structure (Unchanged) --- */}
        <Box
          sx={{
            px: 3,
            pt: 2,
            backgroundColor: '#FFFFFF !important',
          }}
        >
          <Stack direction="row" spacing={3}>
            <Box
              sx={{
                display: 'inline-block',
                color: '#aaa !important',
                cursor: 'not-allowed',
                pb: 1,
                borderBottom: '4px solid transparent',
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 'bold',
                  textTransform: 'none',
                  color: '#aaa !important'
                }}
              >
                DMO Metrics
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'inline-block',
                color: '#aaa !important',
                cursor: 'not-allowed',
                pb: 1,
                borderBottom: '4px solid transparent',
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 'bold',
                  textTransform: 'none',
                  color: '#aaa !important'
                }}
              >
                NPI Dashboards
              </Typography>
            </Box>

            <Box
              onClick={() => handleTabClick('snow')}
              sx={{
                display: 'inline-block',
                color: '#0070C0 !important',
                cursor: 'pointer',
                pb: 1,
                borderBottom: '4px solid #0070C0',
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 'bold',
                  textTransform: 'none',
                  color: '#0070C0 !important'
                }}
              >
                SNOW Assignment Groups
              </Typography>
            </Box>
          </Stack>
          <Divider sx={{ mt: 0.5, borderColor: 'rgba(0, 0, 0, 0.12) !important' }} />
        </Box>
        {/* --- END OF TAB --- */}


        {activeTab === 'snow' && (
          <Box sx={{ p: 3 }}>

            {/* --- FIX: Added mt:3 and changed mb:6 to mb:3 --- */}
            <Box sx={{ mt: 3, mb: 3 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="filter-type-label" sx={lightThemeInputStyles.label}>Filter By</InputLabel>
                    <Select
                      labelId="filter-type-label"
                      label="Filter By"
                      value={filterType}
                      onChange={handleFilterTypeChange}
                      sx={lightThemeSelectStyles}
                      MenuProps={forceLightMenuProps}
                    >
                      {filterTypeOptions.map((option) => (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={5}>
                  {isSearchable ? (
                    <Autocomplete
                      freeSolo
                      options={assignmentGroupOptions}
                      filterOptions={filterOptions}
                      value={filterValue}
                      onChange={(event, newValue) => {
                        setFilterValue(newValue || '');
                      }}
                      onInputChange={(event, newInputValue) => {
                        setFilterValue(newInputValue || '');
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Search Assignment Group"
                          size="small"
                          sx={lightThemeInputStyles}
                        />
                      )}
                      PaperComponent={(props) => (
                        <Paper {...props} sx={lightThemeMenuPropsAuto.PaperProps.sx} />
                      )}
                    />
                  ) : (
                    <FormControl fullWidth size="small">
                      <InputLabel id="value-select-label" sx={lightThemeInputStyles.label}>Select Value</InputLabel>
                      <Select
                        labelId="value-select-label"
                        label="Select Value"
                        value={filterValue}
                        onChange={handleFilterValueChange as (event: SelectChangeEvent<string>) => void}
                        sx={lightThemeSelectStyles}
                        MenuProps={forceLightMenuProps}
                      >
                        {filterValueOptions.map((option) => (
                          <MenuItem
                            key={option}
                            value={option}
                          >
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Grid>

                <Grid item xs={12} sm={3} sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleQuery}
                    startIcon={<SearchIcon />}
                    sx={{
                      backgroundColor: '#0070C0',
                      color: '#FFFFFF !important',
                      '&:hover': { backgroundColor: '#005a9c' },
                      textTransform: 'none'
                    }}
                  >
                    Query
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleClearFilter}
                    startIcon={<ClearIcon />}
                    sx={{
                      textTransform: 'none',
                      color: '#555 !important',
                      borderColor: '#CCC !important'
                    }}
                  >
                    Clear
                  </Button>
                </Grid>
              </Grid>
            </Box>
            {/* --- END Search & Filter Section --- */}


            {/* 4. Title and "Add New" Button (Unchanged) --- */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333 !important' }}>
                Details
              </Typography>
              <Box>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddNew}
                  sx={{
                    mr: 2,
                    backgroundColor: '#0070C0',
                    color: '#FFFFFF !important',
                    '&:hover': { backgroundColor: '#005a9c' },
                    textTransform: 'none',
                    borderRadius: '16px',
                    boxShadow: 'none',
                    fontSize: '14px'
                  }}
                >
                  Add New
                </Button>
              </Box>
            </Box>

            {/* Loading and Error Handlers (Unchanged) */}
            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', my: 5 }}>
                <CircularProgress sx={{ color: '#0070C0' }} />
                <Typography sx={{ ml: 2, color: '#333 !important' }}>Loading Data...</Typography>
              </Box>
            )}
            {error && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', my: 5, color: 'red' }}>
                <ErrorIcon sx={{ mr: 1 }} />
                <Typography color="error">Error: {error}</Typography>
              </Box>
            )}

            {/* --- CustomizedDataGrid (Unchanged) --- */}
            {!isLoading && !error && (
              <CustomizedDataGrid
                tableColumns={getColumns()}
                tableData={paginatedRows}
                onSorting={handleSorting}
                currSorting={sortingInfo}
                page={page + 1}
                pageSize={rowsPerPage}
                paginationData={{
                  totalElements: rows.length,
                  totalPages: Math.ceil(rows.length / rowsPerPage),
                  currentPage: page + 1,
                  size: rowsPerPage
                }}
                handlepageSizeChange={handleChangeRowsPerPage}
                handlePageChange={handleChangePage}
                tableType="Summary"
                defaultColumnNames={['ASSIGNMENT GROUP', 'DOMAIN', 'PROGRAM', 'BUSINESS UNIT', 'DEPARTMENT', 'CAPABILITY', 'ACTIONS']}
                listLength={paginatedRows.length}
                isDisabled={isLoading}
                isDateRangeFilter={false}
              />
            )}
            {/* --- END OF TABLE --- */}

          </Box>
        )}

        {/* Placeholders for other tabs (Unchanged) */}
        {activeTab === 'dmo' && (
          <Box sx={{ p: 3 }}>
            <Typography sx={{ color: '#333 !important' }}>DMO Metrics Page Content</Typography>
          </Box>
        )}
        {activeTab === 'npi' && (
          <Box sx={{ p: 3 }}>
            <Typography sx={{ color: '#333 !important' }}>NPI Dashboards Page Content</Typography>
          </Box>
        )}

      </Paper>
    </Box>
  );
};

export default Governance;